import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, authedFileUrl } from "../api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Receipt as ReceiptIcon, Check, X, Image as ImageIcon } from "lucide-react";

const STATUS_PILL = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
  paid: "bg-slate-900 text-white border-slate-900",
};

export default function Expenses() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  // Per-row toggle for "pay in next pay run" when approving.
  const [autoReim, setAutoReim] = useState({});

  const load = async (status) => {
    try {
      const r = await api.get("/expenses", { params: status === "all" ? {} : { status } });
      setRows(r.data || []);
    } catch (e) { toast.error(errMsg(e, "Failed to load expenses")); }
  };

  useEffect(() => { load(tab); }, [tab]);

  const review = async (exp, status) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/expenses/${exp.expense_id}`, {
        status,
        auto_reimburse: status === "approved" ? !!autoReim[exp.expense_id] : false,
      });
      toast.success(status === "approved"
        ? `Approved${autoReim[exp.expense_id] ? " · will auto-pay on next pay run" : ""}`
        : "Rejected");
      await load(tab);
    } catch (e) {
      toast.error(errMsg(e, "Review failed"));
    } finally { setBusy(false); }
  };

  const receiptUrl = (id) => `${authedFileUrl(`/expenses/${id}/receipt`)}`;

  return (
    <div className="space-y-5" data-testid="expenses-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ReceiptIcon size={22} /> Driver Expenses
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Review receipts drivers submit from the mobile portal. Approve & tick <em>Pay in next run</em> to auto-add as a reimbursement line on the driver's next pay stub.
          </p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {["pending", "approved", "rejected", "paid", "all"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`expenses-tab-${t}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              tab === t
                ? "border-amber-500 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Mobile card view (phones) */}
      <div className="md:hidden space-y-3">
        {rows.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
            No {tab === "all" ? "" : tab} expenses yet.
          </div>
        )}
        {rows.map((e) => (
          <div
            key={e.expense_id}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
            data-testid={`exp-card-${e.expense_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{e.driver_name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{e.driver_display_id}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{(e.submitted_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-bold text-base text-slate-900">${Number(e.amount || 0).toFixed(2)}</div>
                <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${STATUS_PILL[e.status] || ""}`}>
                  {e.status}
                  {e.status === "approved" && e.auto_reimburse && " · auto-pay"}
                </span>
              </div>
            </div>

            <div className="mt-2 text-xs text-slate-600 space-y-0.5">
              <div><span className="text-slate-400">Category:</span> {e.category || "—"}</div>
              {e.note && <div className="break-words"><span className="text-slate-400">Note:</span> {e.note}</div>}
              {e.company_paid && e.company_card && (
                <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-blue-800 text-[10px] font-semibold">
                  Company card · {e.company_card.label} •••{e.company_card.last4}
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:underline"
                onClick={() => setViewing(e)}
                data-testid={`exp-view-m-${e.expense_id}`}
              >
                <ImageIcon size={14} /> View receipt
              </button>
            </div>

            {e.status === "pending" && (
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                {!e.company_paid && (
                  <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={!!autoReim[e.expense_id]}
                      onCheckedChange={(v) => setAutoReim((p) => ({ ...p, [e.expense_id]: !!v }))}
                      data-testid={`exp-autoreim-m-${e.expense_id}`}
                    />
                    Pay in next run
                  </label>
                )}
                {e.company_paid && (
                  <div className="text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded px-2 py-1">
                    Company card charge — approving just logs it (no effect on driver pay).
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    className="h-9 bg-emerald-600 hover:bg-emerald-700 w-full"
                    onClick={() => review(e, "approved")}
                    data-testid={`exp-approve-m-${e.expense_id}`}
                  >
                    <Check size={14} className="mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 border-rose-300 text-rose-700 hover:bg-rose-50 w-full"
                    onClick={() => review(e, "rejected")}
                    data-testid={`exp-reject-m-${e.expense_id}`}
                  >
                    <X size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop / tablet table view */}
      <div className="hidden md:block rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="min-w-[900px] w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-4 py-2.5">Submitted</th>
              <th className="px-4 py-2.5">Driver</th>
              <th className="px-4 py-2.5">Category</th>
              <th className="px-4 py-2.5">Note</th>
              <th className="px-4 py-2.5 text-right">Amount</th>
              <th className="px-4 py-2.5">Receipt</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 w-[320px]">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500">
                No {tab === "all" ? "" : tab} expenses yet.
              </td></tr>
            )}
            {rows.map((e) => (
              <tr key={e.expense_id} className="border-t border-slate-100" data-testid={`exp-row-${e.expense_id}`}>
                <td className="px-4 py-2 text-xs text-slate-500">{(e.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-2">
                  <div className="font-medium text-slate-900">{e.driver_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{e.driver_display_id}</div>
                </td>
                <td className="px-4 py-2 text-slate-700">
                  {e.category || "—"}
                  {e.company_paid && e.company_card && (
                    <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-blue-800 text-[10px] font-semibold">
                      Company · {e.company_card.label} •••{e.company_card.last4}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-700 max-w-[220px] truncate" title={e.note}>{e.note || "—"}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">${Number(e.amount || 0).toFixed(2)}</td>
                <td className="px-4 py-2">
                  <button
                    className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline"
                    onClick={() => setViewing(e)}
                    data-testid={`exp-view-${e.expense_id}`}
                  >
                    <ImageIcon size={14} /> View
                  </button>
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold ${STATUS_PILL[e.status] || ""}`}>
                    {e.status}
                    {e.status === "approved" && e.auto_reimburse && " · auto-pay"}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {e.status === "pending" ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      {!e.company_paid && (
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                          <Checkbox
                            checked={!!autoReim[e.expense_id]}
                            onCheckedChange={(v) => setAutoReim((p) => ({ ...p, [e.expense_id]: !!v }))}
                            data-testid={`exp-autoreim-${e.expense_id}`}
                          />
                          Pay in next run
                        </label>
                      )}
                      {e.company_paid && (
                        <span className="text-[11px] text-blue-700" title="Company card charge — approving just logs it, no pay impact">
                          Company card · log only
                        </span>
                      )}
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => review(e, "approved")} data-testid={`exp-approve-${e.expense_id}`}>
                        <Check size={13} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => review(e, "rejected")} data-testid={`exp-reject-${e.expense_id}`}>
                        <X size={13} className="mr-1" /> Reject
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Receipt — {viewing?.driver_name} · ${Number(viewing?.amount || 0).toFixed(2)}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div>
              <div className="mb-2 text-xs text-slate-500">
                <strong>{viewing.category || "No category"}</strong>
                {viewing.note && <> · {viewing.note}</>}
              </div>
              <img
                src={receiptUrl(viewing.expense_id)}
                alt="Receipt"
                className="w-full max-h-[70vh] object-contain rounded border border-slate-200 bg-slate-50"
                data-testid="exp-receipt-img"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
